import { match } from "ts-pattern";

import { MsButton, MsLink } from "@theme/components";

import { getInternalOrExternalLink } from "@theme/utils";

import type { StrapiLinkInterface } from "@ms/gatsby-site/src/types/strapi";
import type * as buttonVariants from "@ms/gatsby-theme/src/themes/ms5/variants/buttons";
import type * as linkVariants from "@ms/gatsby-theme/src/themes/ms5/variants/links";
import type { ThemeUIStyleObject } from "@theme/components";
import type { FC, SVGProps } from "react";

export interface StrapiLinkProps extends StrapiLinkInterface {
  linkVariant?: keyof typeof linkVariants;
  buttonVariant?: keyof typeof buttonVariants;
  TrailingIcon?: FC<SVGProps<SVGElement>>;
  className?: string;
  sx?: ThemeUIStyleObject;
  isBlog?: boolean;
  onClick?: () => void;
  role?: string;
}

/**
 * Extract Link
 *
 * This helper function takes in a StrapiLinkInterface and returns the path
 *
 * @param {StrapiLinkInterface} link
 * @returns {string} path
 */
export const extractLink = (link: StrapiLinkProps) => {
  const templateSlug = link.templatePage?.slug;
  if (templateSlug === "home") return "/";
  if (templateSlug) return `/pages/${templateSlug}`;
  if (link.templateLegal?.slug) return `/pages/${link.templateLegal.slug}`;

  return link.url;
};

export const StrapiLink: FC<StrapiLinkProps> = (props) => {
  const {
    isBlog,
    label,
    newWindow,
    customAction,
    buttonVariant,
    linkVariant,
    ariaLabel,
    TrailingIcon,
    onClick,
    className,
    role,
    sx,
  } = props;
  const to = extractLink(props);

  const customActionHandler = () => {
    if (!customAction) return;

    match(customAction)
      .with("OPEN MODAL: Ketch Preferences", () => {
        window.ketch && window.ketch("showPreferences");
      })
      .with("OPEN MODAL: Withdrawal from Contract", () => {
        console.log("This should not fire. Modal actions are being handled in gatsby site only");
      })
      .exhaustive();
  };

  if (customAction) {
    return (
      <MsButton
        variant={buttonVariant ?? "link"}
        sx={{ textAlign: "left", ...sx }}
        onClick={customActionHandler}
        aria-label={ariaLabel ?? undefined}
        role={role}
      >
        {label}
      </MsButton>
    );
  }

  if (to) {
    const href = isBlog ? (getInternalOrExternalLink(to, isBlog) ?? "") : to;

    return (
      <MsLink
        className={className}
        to={href}
        variant={linkVariant ?? "link"}
        aria-label={ariaLabel ?? undefined}
        target={newWindow ? "_blank" : undefined}
        rel={newWindow ? "noopener noreferrer" : undefined}
        sx={sx}
        onClick={onClick}
      >
        {label}
        {TrailingIcon && <TrailingIcon />}
      </MsLink>
    );
  }

  return null;
};
